import { ROUTES, routeTemplateToRegex } from '@repo/shared-types';

type RouteKey = keyof typeof ROUTES;

/**
 * Table-driven path → active nav id overrides for URLs that are not resolved by
 * the longest-prefix match against flat nav leaves (param routes, alias paths).
 *
 * Prefer `{ kind: 'template', routeKey }` so patterns stay tied to {@link ROUTES}.
 * Use `{ kind: 'regex' }` only when the match is not expressible as a single ROUTES template
 * (e.g. negative lookahead excluding reserved subpaths).
 */
export type NavPathActiveOverride =
  | { readonly kind: 'template'; readonly routeKey: RouteKey; readonly navId: string }
  | { readonly kind: 'regex'; readonly regex: RegExp; readonly navId: string; readonly note: string };

export const NAV_PATH_ACTIVE_OVERRIDES: readonly NavPathActiveOverride[] = [
  { kind: 'template', routeKey: 'OPS_ACCOUNTING_BILLS', navId: 'accounting.bills' },
  { kind: 'template', routeKey: 'VENDOR_JOB_DETAIL', navId: 'vendor.jobs.overview' },
  { kind: 'template', routeKey: 'OWNER_PROPERTY_DETAIL', navId: 'owners.properties' },
  { kind: 'template', routeKey: 'OWNER_BACKOFFICE_PROPERTY_DETAIL', navId: 'owners.properties' },
  { kind: 'template', routeKey: 'INVESTOR_PROPERTY_DETAIL', navId: 'investment.programs' },
  {
    kind: 'regex',
    regex: /^\/app\/properties\/(?!owners|units|portfolios)[^/]+$/,
    navId: 'properties.directory',
    note: 'Property detail excludes /owners, /units, /portfolios subpaths (not a single ROUTES template).',
  },
  { kind: 'template', routeKey: 'PROJECT_DETAIL', navId: 'maintenance.projects' },
  { kind: 'template', routeKey: 'INSPECTION_DETAIL', navId: 'maintenance.inspections' },
  { kind: 'template', routeKey: 'VENDOR_DETAIL', navId: 'vendors.directory' },
  { kind: 'template', routeKey: 'TENANT_DETAIL', navId: 'tenants.directory' },
  { kind: 'template', routeKey: 'OWNER_BACKOFFICE_PROPERTIES', navId: 'owners.properties' },
  { kind: 'template', routeKey: 'OWNER_DETAIL', navId: 'owners.management' },
  { kind: 'template', routeKey: 'INVESTMENT_DETAIL', navId: 'investment.programs' },
  { kind: 'template', routeKey: 'ACCOUNTING_PAYMENT_PLANS', navId: 'accounting.payments' },
  { kind: 'template', routeKey: 'ACCOUNTING_PURCHASE_ORDERS', navId: 'accounting.bills' },
  { kind: 'template', routeKey: 'ACCOUNTING_THREE_WAY_MATCH', navId: 'accounting.bills' },
  { kind: 'template', routeKey: 'ACCOUNTING_BANK_OPS', navId: 'accounting.reconciliation' },
  { kind: 'template', routeKey: 'ACCOUNTING_DISTRIBUTIONS', navId: 'accounting.close' },
] as const;

export function getNavPathOverrideMatchers(): ReadonlyArray<{ readonly test: (path: string) => boolean; readonly navId: string }> {
  return NAV_PATH_ACTIVE_OVERRIDES.map((entry) => {
    if (entry.kind === 'template') {
      const pattern = routeTemplateToRegex(ROUTES[entry.routeKey]);
      return { test: (p: string) => pattern.test(p), navId: entry.navId };
    }
    return { test: (p: string) => entry.regex.test(p), navId: entry.navId };
  });
}
